import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { amplitudeModule } from '@/modules/amplitude';
import {
  OnboardingOptionItem,
  OnboardingOptionsList,
} from '@/pages/Onboarding/components/List';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { useOnboarding } from '@/pages/Onboarding/context';
import { OwnAiTitle } from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { getIdentifyCategories } from '../../utils/getIdentifyCategories';

import { IDENTIFY_OPTIONS } from './constants';
import { IdentifyStyles, IdentifySubtitle } from './Identify.styles';

import type { LayoutDataType } from '../../types';

export const ScreenIdentify: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Identify',
      });
    }
  }, []);

  const { wizard } = useOnboarding();
  const form = useFormContext<LayoutDataType>();
  const gender = form.watch('gender');
  const identify = form.watch('identify');

  const handleChange = (option: OnbOptionType<LayoutDataType['identify']>) => {
    form.setValue('identify', option.id);
    amplitudeModule.logEvent('onboarding_gender_selected', {
      choice: option.id,
    });
    const mapping = getIdentifyCategories(gender, option.id);
    userService.createOrUpdate({
      ...(mapping && {
        categories: mapping.categories,
      }),
      onboarding_data: {
        identify: option.id,
        ...(mapping && { first_categories: mapping.first_categories }),
      },
    });
    nextStepTimeout(wizard.nextStep);
  };

  return (
    <IdentifyStyles>
      <OnboardingNavigation />

      <IonContent>
        <OwnAiTitle>How do you identify?</OwnAiTitle>
        <IdentifySubtitle variant="body-large" color="neutral-white">
          This helps us create interactions that feel right for you.
        </IdentifySubtitle>
        <OnboardingOptionsList>
          {IDENTIFY_OPTIONS.map((option) => (
            <OnboardingOptionItem
              checked={identify === option.id}
              control={form.control}
              option={option}
              name="identify"
              key={option.id}
              onChange={() => handleChange(option)}
              TEST_ID={`identify-${option.id}`}
            />
          ))}
        </OnboardingOptionsList>
      </IonContent>
    </IdentifyStyles>
  );
};
